/**
 * Plays a shell session's recording in the browser (stage 2).
 *
 * The same audited route the Recording download uses
 * (`GET /api/remote-sessions/:id/recording`), so pressing Play is recorded
 * in the audit log exactly as a download is — watching a recording is
 * reading it. asciinema-player (Apache-2.0) does the playback.
 */

import * as AsciinemaPlayer from 'asciinema-player';
import 'asciinema-player/dist/bundle/asciinema-player.css';
import { useEffect, useState } from 'react';

import { Banner, Drawer } from './components/index.ts';

export function RecordingPlayer({
  sessionId,
  title,
  onClose,
}: {
  sessionId: string;
  title: string;
  onClose: () => void;
}): React.JSX.Element {
  /*
   * A callback ref held in state, not useRef: the drawer renders its content
   * through a portal a render later, so on the first effect the container does
   * not exist yet — and an effect keyed only on the session never ran again,
   * leaving an empty drawer. Found by the e2e spec.
   */
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (el === null) return undefined;
    let player: AsciinemaPlayer.Player | null = null;
    let gone = false;
    /*
     * Fetched here rather than handed to the player as a URL, so a refusal —
     * a 403 for a viewer, a 404 for a diagnostic session — says why instead
     * of leaving an empty player.
     */
    void (async () => {
      try {
        const res = await fetch(`/api/remote-sessions/${sessionId}/recording`, { credentials: 'same-origin' });
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? `the recording could not be loaded (${res.status})`);
        }
        const cast = await res.text();
        if (gone) return;
        const mono = getComputedStyle(document.documentElement).getPropertyValue('--mono').trim();
        player = AsciinemaPlayer.create({ data: cast }, el, {
          fit: 'width',
          // Long silences (someone reading the screen) are shortened to two
          // seconds on playback; the file itself keeps the real timing.
          idleTimeLimit: 2,
          ...(mono !== '' ? { terminalFontFamily: mono } : {}),
          controls: true,
          autoPlay: true,
        });
      } catch (err) {
        if (!gone) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      gone = true;
      player?.dispose();
    };
  }, [sessionId, el]);

  return (
    <Drawer open onClose={onClose} width="lg" title={title} subtitle="Played back as it was shown. Pauses over two seconds are shortened.">
      {error !== null && <Banner tone="fault">{error}</Banner>}
      <div ref={setEl} className="remote-player" aria-label="Session recording" />
    </Drawer>
  );
}
